/**
 * 功能導覽內容
 *
 * ⚠️ 這份清單要與 components/layout/Layout.tsx 的 navigationGroups 對得上：
 * 新增／移除側欄分頁時，這裡也要同步更新，否則新人看到的導覽會與實際選單不符。
 * roles 未指定＝所有人都看得到；有指定則只有該角色會在導覽中看到這一項。
 */

export type TourRole = 'ADMIN' | 'MANAGER' | 'EMPLOYEE'

export interface TourItem {
  label: string
  path: string
  /** 一句話說明「這頁是做什麼的」 */
  description: string
  /** 新人特別容易搞錯的地方，沒有就不顯示 */
  tip?: string
  roles?: TourRole[]
}

export interface TourSection {
  title: string
  items: TourItem[]
}

export const TOUR_SECTIONS: TourSection[] = [
  {
    title: '時間管理',
    items: [
      {
        label: '計時器',
        path: '/timer',
        description: '上工按開始、下班按結束，順手寫下這段時間在做什麼。',
        tip: '這是每天最常用的一頁。計時只是「記錄」，還不等於申報。'
      },
      {
        label: '工時申報',
        path: '/timesheets',
        description: '把一週的計時紀錄整理後送出，交給主管審核。',
        tip: '沒有送出申報就不會進入計薪流程——每週記得送一次。'
      },
      {
        label: '虛擬辦公室',
        path: '/office',
        description: '看看現在有哪些夥伴在線上工作，也可以佈置自己的座位。'
      }
    ]
  },
  {
    title: '發款與請款',
    items: [
      {
        label: '發款紀錄',
        path: '/salaries',
        description: '每月的薪資明細、加給與發放狀態都在這裡查。'
      },
      {
        label: '福利中心',
        path: '/benefits',
        description: '申請公司提供的福利獎勵，送出後由管理員審核。'
      },
      {
        label: '費用申報',
        path: '/expenses',
        description: '自己先代墊的錢在這裡申請核銷，記得附上收據照片。'
      },
      {
        label: '我的獎金湖',
        path: '/bonus-pool',
        description: '查看專案獎金的累積與分配情況。'
      }
    ]
  },
  {
    title: '成長與績效',
    items: [
      {
        label: '夥伴升等',
        path: '/partner-map',
        description: '職等地圖：看得到每一級的條件，以及自己現在的位置。'
      },
      {
        label: '目標達成',
        path: '/my-goals',
        description: '這個月的 KPI 目標與完成度。'
      },
      {
        label: '事蹟卡片',
        path: '/performance',
        description: '累積的功勞卡與改善卡紀錄，是升等審核的重要依據。'
      }
    ]
  },
  {
    title: '文件與紀錄',
    items: [
      {
        label: '公司公告',
        path: '/announcements',
        description: '公司制度與重要規範。',
        tip: '標示「必讀」的公告沒看完，計時器會被鎖住。'
      },
      {
        label: '我的文件',
        path: '/documents',
        description: '需要你簽署的合約與文件都會出現在這裡。',
        tip: '逾期未簽會鎖住計時器，收到通知就盡快處理。'
      },
      {
        label: '報表分析',
        path: '/reports',
        description: '工時與成本的統計圖表。'
      },
      {
        label: '設定',
        path: '/settings',
        description: '個人資料、銀行帳戶、身分證影本與外觀主題。',
        tip: '銀行帳戶沒填，發薪會卡住；生日與身分證填了就只有管理員能改，請仔細確認。'
      }
    ]
  },
  {
    title: '審批（主管 / 管理員）',
    items: [
      {
        label: '工時審批',
        path: '/approvals/timesheets',
        description: '審核夥伴送出的每週工時申報。',
        roles: ['ADMIN', 'MANAGER']
      },
      {
        label: '福利審批',
        path: '/admin/benefit-approvals',
        description: '審核福利申請。',
        roles: ['ADMIN']
      },
      {
        label: '費用審核',
        path: '/admin/expense-approvals',
        description: '審核代墊款核銷申請。',
        roles: ['ADMIN']
      },
      {
        label: '月申報審批',
        path: '/approvals/salaries',
        description: '審核每月薪資申報。',
        roles: ['ADMIN']
      },
      {
        label: '文件審批',
        path: '/admin/documents/signatures',
        description: '審核夥伴簽署的文件。',
        roles: ['ADMIN']
      }
    ]
  },
  {
    title: '後台管理（管理員）',
    items: [
      {
        label: '用戶管理',
        path: '/users',
        description: '新增夥伴、設定職等與時薪、管理銀行資訊。',
        tip: '新夥伴要先在「恆遠會員中樞」有帳號才能登入，建立時可勾選一併開通。',
        roles: ['ADMIN']
      },
      {
        label: '專案管理',
        path: '/projects',
        description: '建立專案與成員，計時要選專案才好分析成本。',
        roles: ['ADMIN', 'MANAGER']
      },
      {
        label: '文件管理',
        path: '/admin/documents/templates',
        description: '建立文件範本，並設定哪一職等要簽哪些文件。',
        tip: '設好職等規則後，新夥伴建立時就會自動收到該簽的文件。',
        roles: ['ADMIN']
      },
      {
        label: '目標設定',
        path: '/admin/goals',
        description: '設定夥伴每月的 KPI 目標。',
        roles: ['ADMIN']
      },
      {
        label: '獎金池管理',
        path: '/admin/bonus-pools',
        description: '管理專案獎金池與分配。',
        roles: ['ADMIN']
      },
      {
        label: '固定加給',
        path: '/admin/allowances',
        description: '設定每月固定發放的加給項目。',
        roles: ['ADMIN']
      },
      {
        label: '事蹟管理',
        path: '/admin/performance',
        description: '發放功勞卡、開立績效改善卡。',
        roles: ['ADMIN']
      },
      {
        label: '公告管理',
        path: '/admin/announcements',
        description: '發布公司公告，可設為必讀。',
        roles: ['ADMIN']
      }
    ]
  }
]

/** 依角色過濾出該使用者實際看得到的導覽內容（空的分組會被移除） */
export const getTourSectionsForRole = (role?: string): TourSection[] =>
  TOUR_SECTIONS.map(section => ({
    ...section,
    items: section.items.filter(item => !item.roles || item.roles.includes(role as TourRole))
  })).filter(section => section.items.length > 0)
